import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import useDelayUnmount from "../../../hooks/useDelayUnmount";
import classes from "./Select.module.css";
import ValidationErrors from "../ValidationErrors/ValidationErrors";
import ClearIcon from "@mui/icons-material/Clear";
import validate from "../../../utils/validations/validateV2";
import { ReactComponent as ArrowDown } from "../../../images/icons/forms/arrowSquareDown.svg";
import MultiLanTxt from "../MultiLanTxt/MultiLanTxt";
import checkFieldDisabledStatus from "../../../utils/accessUtils/checkFieldDisabledStatus";
import extractFieldValidations from "../../../utils/accessUtils/extractFieldValidations";
import { createPortal } from "react-dom";

const Select = ({
  name,
  edit,
  value,
  options,
  onChange,
  disabled,
  processualDisabled,
  pageIsProcessual,
  entityIsProcessual,
  skipStateCanEditThisFieldCheck,
  label,
  validityName,
  optionPreferedKey,
  uniqueLabelId,
  fieldAccesses = [],
  fieldNames = [],
  fieldName,
  style,
  deletable = true,
  valueType,
  forceLtr,
  currentState,
  beforeOpenOptionsFunc,
  localValidationInfos = {},
  forceEn,
  fieldAddress,
  confirmActionOrder,
  validateHandler,
  forcePe,
  onFocus,
  onBlur,
  skipFieldAccessCheck,
  booleanOptions,
  isMongoField,
  hasSQLEntity,
  mongoFieldPath,
  withStar = true,
}) => {
  const [open, setOpen] = useState(false);
  const showOptions = useDelayUnmount(open, 250);
  const [focusOn, setFocusOn] = useState(0);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);

  const { disabled: fieldIsDisabled, processualDisabled: fieldIsProcessualDisabled } = useMemo(
    () =>
      checkFieldDisabledStatus({
        fieldAccesses,
        fieldName,
        pageIsProcessual,
        disabledFromParent: disabled,
        processualDisabledFromParent: processualDisabled,
        currentState,
        entityIsProcessual,
        skipStateCanEditThisFieldCheck,
        isMongoField,
        hasSQLEntity,
        mongoFieldPath: mongoFieldPath ? mongoFieldPath : fieldAddress ? fieldAddress : null,
      }),
    [fieldAccesses, disabled, processualDisabled, currentState],
  );
  const { inWhichStateOrderItBecomesNecessary, validationType, isMandatory } = useMemo(
    () =>
      extractFieldValidations({
        fieldAccesses,
        fieldName,
      }),
    [fieldAccesses],
  );

  useEffect(() => {
    if (edit && options?.length > 0 && value) {
      let foundOptiondIndex = options.findIndex((op) => {
        if (typeof op === "object") {
          return op?.id === value?.id;
        } else {
          return op === value;
        }
      });
      setFocusOn(foundOptiondIndex ? foundOptiondIndex : 0);
    }
  }, [options, value]);

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    };
    window.addEventListener("scroll", update, true); // true = capture, catches inner scroll containers too
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  const optionClickHandler = (option, index) => {
    let preferedObj = { target: { value: option, name } };
    onChange(preferedObj, fieldAddress);
    if (validityName)
      if (validateHandler) {
        // if (!validityName?.isValid) {
        const validateOptions = { ...localValidationInfos };
        if (isMandatory) {
          validateOptions.isOptional = false;
        } else {
          validateOptions.isOptional = true;
        }
        // if (validationType) validateOptions.type = validationType; // SINCE THERE WILL BE NO TYPE FOR SELECT
        if (inWhichStateOrderItBecomesNecessary)
          validateOptions.inWhichStateOrderItBecomesNecessary = inWhichStateOrderItBecomesNecessary;
        if (confirmActionOrder) validateOptions.clickedActionOrder = confirmActionOrder;
        validateOptions.data = option;

        const newValue = validate(validateOptions);
        validateHandler(newValue, fieldAddress);
      }
    // }
    setFocusOn(index);
    setOpen(false);
    if (onBlur) onBlur(preferedObj, fieldAddress);
  };

  const renderBooleanProperTxt = (option) => {
    let returnedVal =
      option === true ? (
        <MultiLanTxt tid={booleanOptions.find((op) => op.value === true).labelKey} />
      ) : option === false ? (
        <MultiLanTxt tid={booleanOptions.find((op) => op.value === false).labelKey} />
      ) : (
        ""
      );

    return returnedVal;
  };
  const renderProperTxt = (option) => {
    let returnedVal;
    if (typeof option === "string" || typeof option === "number") {
      returnedVal = option;
    } else {
      if (optionPreferedKey) {
        returnedVal = option[optionPreferedKey];
      } else {
        returnedVal = option?.name
          ? option.name
          : option?.title
            ? option.title
            : //  "which key should be shown";
              "";
      }
    }

    return returnedVal;
  };
  const renderProperTxtFromOptions = (option) => {
    let foundedOption = options.find((which) => which.value === option);
    let properTxt = foundedOption?.name;
    return properTxt;
  };
  const openOptionsHandler = () => {
    const preferedObj = { target: { value, name } };
    if (onFocus) onFocus(preferedObj, fieldAddress);
    if (beforeOpenOptionsFunc) beforeOpenOptionsFunc();
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
    setOpen(true);
  };
  const blurOnTabClick = (e) => {
    const preferedObj = { target: { value, name } };
    if (e.key === "Tab") {
      setOpen(false);
      if (onBlur) onBlur(preferedObj, fieldAddress);
    }
  };
  const backdropClickHandler = (e) => {
    const preferedObj = { target: { value, name } };
    setOpen(false);
    if (onBlur) onBlur(preferedObj, fieldAddress);
  };
  const readOnly = skipFieldAccessCheck ? disabled : fieldIsDisabled || fieldIsProcessualDisabled;
  return (
    <>
      {open
        ? createPortal(<div className={classes.bckdrp} onClick={backdropClickHandler} />, document.body)
        : null}
      <div
        ref={triggerRef}
        className={[classes.container, style === "tableSearch" ? classes.fullHeight : null].join(" ")}
      >
        <span
          className={[
            classes.labelsContainer,
            readOnly
              ? [classes.disabledCursor, "disabledinputPLabel"].join(" ")
              : open
                ? "focusedinputPLabel"
                : validityName
                  ? validityName?.isValid
                    ? "enabledinputPLabel"
                    : "invalidinputPLabel"
                  : "enabledinputPLabel",
          ].join(" ")}
          onClick={(e) => (!readOnly ? openOptionsHandler() : null)}
        >
          <label
            className={[
              classes.label,
              (booleanOptions ? value !== null : value)
                ? [classes.labelUp, "pLabelUp"].join(" ")
                : [classes.labelDown, "pLabelDown"].join(" "),
            ].join(" ")}
            htmlFor={uniqueLabelId ? uniqueLabelId : name}
            onClick={(e) => (!readOnly ? openOptionsHandler() : null)}
          >
            {label}
            {withStar ? (
              isMandatory || localValidationInfos?.isOptional === false ? (
                <span className={classes.mandatorySign}>*</span>
              ) : null
            ) : null}
          </label>
          {!readOnly ? (
            deletable && (booleanOptions ? value !== null : value) ? (
              <label
                className={[
                  style === "tableSearch"
                    ? [classes.tableSearchArrowLabel, "disabledinputPLabel"].join(" ")
                    : [
                        classes.arrowLabel,
                        readOnly
                          ? "disabledinputPLabel"
                          : open
                            ? "focusedinputPLabel"
                            : validityName?.isValid === false
                              ? "invalidinputPLabel"
                              : "enabledinputPLabel",
                      ].join(" "),
                  open ? classes.reversedIcon : null,
                ].join(" ")}
                htmlFor={uniqueLabelId ? uniqueLabelId : name}
                onClick={(e) => {
                  let preferedObj = { target: { value: null, name } };
                  e.stopPropagation();
                  onChange(preferedObj, fieldAddress);
                }}
              >
                <ClearIcon />
              </label>
            ) : (
              <label
                className={[
                  style === "tableSearch"
                    ? [classes.tableSearchArrowLabel, "disabledinputPLabel"].join(" ")
                    : [
                        classes.arrowLabel,
                        readOnly
                          ? "disabledinputPLabel"
                          : open
                            ? "focusedinputPLabel"
                            : validityName?.isValid === false
                              ? "invalidinputPLabel"
                              : "enabledinputPLabel",
                      ].join(" "),
                  open ? classes.reversedIcon : null,
                ].join(" ")}
                htmlFor={uniqueLabelId ? uniqueLabelId : name}
              >
                <ArrowDown />
              </label>
            )
          ) : null}
        </span>
        <p
          id={uniqueLabelId ? uniqueLabelId : name}
          className={[
            classes.inheritDir,
            "pInput",
            style === "tableSearch" ? classes.tableSearchSelectInput : classes.selectInput,
            style === "tableSearch"
              ? null
              : readOnly
                ? ["disabledPInput", classes.autoCursor].join(" ")
                : open
                  ? "focusedPInput"
                  : validityName
                    ? validityName?.isValid
                      ? "enabledPInput"
                      : "invalidPInput"
                    : "enabledPInput",
          ].join(" ")}
        >
          {style === "tableSearch" ? (
            value === "" || value === null ? (
              <span className="pLabelPlaceholder">{forceLtr ? "Search" : "جست و جو"}</span>
            ) : valueType === "boolean" ? (
              renderProperTxtFromOptions(value)
            ) : (
              renderProperTxt(value)
            )
          ) : (
            <span className={classes.finalValue}>
              {booleanOptions ? renderBooleanProperTxt(value) : renderProperTxt(value)}
            </span>
          )}
        </p>
        {showOptions
          ? createPortal(
              <div
                className={[classes.optionsContainer, open ? classes.mounted : classes.unmounted].join(
                  " ",
                )}
                style={{
                  position: "absolute",
                  top: coords.top,
                  left: coords.left,
                  width: coords.width,
                  // zIndex: 9999,
                }}
              >
                {options?.length ? (
                  <>
                    {options?.map((option, index) => (
                      <p
                        key={index}
                        className={[
                          "pLabelUp",
                          "enabledinputPLabel",
                          style === "tableSearch" ? classes.tableSearchOptionInput : classes.optionInput,
                          focusOn === index && value ? classes.focusedOption : null,
                        ].join(" ")}
                        onClick={(e) => optionClickHandler(option, index)}
                      >
                        {renderProperTxt(option)}
                      </p>
                    ))}
                  </>
                ) : booleanOptions?.length ? (
                  <>
                    {booleanOptions?.map((booleanOption, index) => (
                      <p
                        key={index}
                        className={[
                          "pLabelUp",
                          "enabledinputPLabel",
                          style === "tableSearch" ? classes.tableSearchOptionInput : classes.optionInput,
                          focusOn === index && value !== null ? classes.focusedOption : null,
                        ].join(" ")}
                        onClick={(e) => optionClickHandler(booleanOption.value, index)}
                      >
                        <MultiLanTxt tid={booleanOption.labelKey} />
                      </p>
                    ))}
                  </>
                ) : (
                  <p className={classes.optionInput}>
                    {forceLtr ? (
                      "No option"
                    ) : forcePe ? (
                      " گزینه ای یافت نشد "
                    ) : (
                      <MultiLanTxt tid="NO_OPTION" />
                    )}
                  </p>
                )}
              </div>,
              document.body,
            )
          : null}
        <input
          className={classes.hiddenInput}
          onFocus={(e) => (!readOnly ? openOptionsHandler() : null)}
          onKeyDown={blurOnTabClick}
          disabled={readOnly}
        />
        {validityName ? (
          <ValidationErrors validity={validityName?.isValid} message={validityName?.msg} />
        ) : null}
      </div>
    </>
  );
};

export default Select;
